import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseDataGridComponent } from '@components/base-data-grid.component';
import { dateFormattingOptions } from '@domain/formatting';
import { ServiceProductItem } from '@hjc/masterdata-types';
import { TranslateModule } from '@ngx-translate/core';
import { ServicesService } from '@services/services.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, RowClickedEvent, themeAlpine } from 'ag-grid-community';
import Fuse from 'fuse.js';
import { debounceTime, Subscription } from 'rxjs';
import { PageHeaderComponent } from '@components/page-header/page-header.component';

@Component({
  selector: 'md-view-services-page',
  imports: [PageHeaderComponent, MatFormFieldModule, MatInputModule, ReactiveFormsModule, MatButtonModule, AgGridAngular, MatIconModule, TranslateModule],
  templateUrl: './view-services-page.component.html',
  styleUrl: './view-services-page.component.scss',
  standalone: true
})
export class ViewServicesPageComponent extends BaseDataGridComponent implements OnInit {
  servicesService = inject(ServicesService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  private gridApi = signal<GridApi<ServiceProductItem> | null>(null);
  isLoading = signal(false);
  isExporting = signal(false);
  allServices = signal<ServiceProductItem[]>([]);
  displayServices = signal<ServiceProductItem[]>([]);
  columnDefs = computed(() => {
    const getTranslatedHeader = (key: string) => (this.translationsReady() ? this.translate.instant(key) : key);

    return [
      { headerName: getTranslatedHeader('FIELDS.CODE'), field: 'id', sortable: true, flex: 1 },
      { headerName: getTranslatedHeader('FIELDS.DESCRIPTION'), valueGetter: p => `${p.data?.name[this.lang()]}`, sortable: true, flex: 2 },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_BY'), valueGetter: p => p.data?.createdBy, sortable: true, filter: true, flex: 2 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_AT'),
        field: 'createdAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 2
      },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_BY'), valueGetter: p => p.data?.updatedBy, sortable: true, filter: true, flex: 2 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_AT'),
        field: 'updatedAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 2
      }
    ] as ColDef<ServiceProductItem>[];
  });

  theme = themeAlpine;
  searchInput = new FormControl<string>('', { nonNullable: true });
  searchTermSubscription?: Subscription;
  fuse?: Fuse<ServiceProductItem>;

  constructor() {
    super();
  }

  async ngOnInit() {
    this.isLoading.set(true);
    const products = (await this.servicesService.getServices())?.data;
    this.allServices.set(products);
    this.displayServices.set(products);

    this.initFuse();
    this.searchTermSubscription = this.searchInput.valueChanges.pipe(debounceTime(300)).subscribe(term => this.search(term));
    this.isLoading.set(false);
  }

  initFuse() {
    this.fuse = new Fuse<ServiceProductItem>(this.allServices(), {
      keys: ['productCode', `name.${this.lang()}`, `productType.name.${this.lang()}`, 'createdBy.name', 'updatedBy.name'],
      threshold: 0.3
    });
  }

  search(term: string) {
    if (!this.fuse) return;
    if (!term || term.length === 0) {
      this.displayServices.set(this.allServices());
      return;
    }
    const results = this.fuse.search(term);
    this.displayServices.set(results.map(r => r.item));
  }

  rowOnClick(event: RowClickedEvent<ServiceProductItem, unknown>) {
    const rowItemId = event.data?.id;
    if (rowItemId) {
      this.router.navigate([rowItemId], { relativeTo: this.route });
    }
  }

  onGridReady(params: { api: GridApi<ServiceProductItem> }) {
    this.gridApi.set(params.api);
  }

  async exportToExcel() {
    const api = this.gridApi();
    if (!api) return;

    await this.exportGridToExcel(api, this.columnDefs(), 'byproducts', this.isExporting);
  }
}
